import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Palette } from 'lucide-react';
import { siteSettings } from '../../data/mock';
import { useTheme } from '../../context/ThemeContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { currentTheme, toggleTheme, themeName } = useTheme();

  const formatHours = (day) => {
    const hours = siteSettings.hours[day];
    if (!hours) return 'Fermé';
    return hours.display || `${hours.open} - ${hours.close}`;
  };

  const daysOfWeek = [
    { key: 'monday', label: 'Lundi' },
    { key: 'tuesday', label: 'Mardi' },
    { key: 'wednesday', label: 'Mercredi' },
    { key: 'thursday', label: 'Jeudi' },
    { key: 'friday', label: 'Vendredi' },
    { key: 'saturday', label: 'Samedi' },
    { key: 'sunday', label: 'Dimanche' },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="space-y-4">
            <Link to="/">
              <img 
                src="/assets/images/logo-footer.webp" 
                alt="Foulées Vertes"
                className="h-16 w-auto"
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p className="text-sm leading-relaxed">
              Spécialiste running, trail et triathlon depuis 2000. 
              Une équipe de passionnés à votre service pour vous conseiller 
              et vous équiper.
            </p>
            <div className="flex gap-3">
              <a
                href={siteSettings.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-700 transition-colors"
              >
                <Facebook size={18} />
              </a>
              <a
                href={siteSettings.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-emerald-700 transition-colors"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Contact</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href={`tel:${siteSettings.contact.phone}`}
                  className="flex items-start gap-3 hover:text-emerald-400 transition-colors"
                >
                  <Phone size={18} className="mt-0.5 flex-shrink-0" />
                  <span>{siteSettings.contact.phoneDisplay || siteSettings.contact.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${siteSettings.contact.email}`}
                  className="flex items-start gap-3 hover:text-emerald-400 transition-colors"
                >
                  <Mail size={18} className="mt-0.5 flex-shrink-0" />
                  <span>{siteSettings.contact.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 flex-shrink-0" />
                <span>{siteSettings.contact.address}</span>
              </li>
            </ul>
          </div>

          {/* Horaires */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
              <Clock size={18} />
              Horaires
            </h3>
            <ul className="space-y-1.5 text-sm">
              <li className="flex justify-between">
                <span>Lundi</span>
                <span>15h - 19h</span>
              </li>
              <li className="flex justify-between">
                <span>Mardi → Samedi</span>
                <span>10h - 13h / 14h - 19h</span>
              </li>
            </ul>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Navigation</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/le-lab" className="hover:text-emerald-400 transition-colors">
                  Le Lab
                </Link>
              </li>
              <li>
                <Link to="/chaussures" className="hover:text-emerald-400 transition-colors">
                  Chaussures
                </Link>
              </li>
              <li>
                <Link to="/textile" className="hover:text-emerald-400 transition-colors">
                  Textile
                </Link>
              </li>
              <li>
                <Link to="/accessoires" className="hover:text-emerald-400 transition-colors">
                  Accessoires
                </Link>
              </li>
              <li>
                <Link to="/nutrition" className="hover:text-emerald-400 transition-colors">
                  Nutrition
                </Link>
              </li>
              <li>
                <Link to="/equipe" className="hover:text-emerald-400 transition-colors">
                  L'Équipe
                </Link>
              </li>
              <li>
                <Link to="/foulees-vertes-co" className="hover:text-emerald-400 transition-colors">
                  FV & Co
                </Link>
              </li>
              <li>
                <Link to="/rendez-vous" className="hover:text-emerald-400 transition-colors">
                  Prendre RDV
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 mt-10 pt-6">
          {/* Theme Switcher */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <Palette size={18} className="text-gray-400" />
            <span className="text-sm text-gray-400">Thème couleur :</span>
            <div className="flex items-center gap-3">
              <span className={`text-sm ${currentTheme === 'emerald' ? 'text-white font-medium' : 'text-gray-500'}`}>
                Émeraude
              </span>
              <button
                onClick={toggleTheme}
                className="relative w-14 h-7 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-emerald-500"
                style={{ 
                  backgroundColor: currentTheme === 'emerald' ? '#047857' : '#B3C43E'
                }}
                aria-label="Changer le thème de couleur"
              >
                <span 
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                    currentTheme === 'ral6039' ? 'left-8' : 'left-1'
                  }`}
                />
              </button>
              <span className={`text-sm ${currentTheme === 'ral6039' ? 'text-white font-medium' : 'text-gray-500'}`}>
                Vert clair
              </span>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm">
            <p>© {currentYear} Foulées Vertes. Tous droits réservés.</p>
            <div className="flex gap-6">
              <Link to="/mentions-legales" className="hover:text-emerald-400 transition-colors">
                Mentions légales
              </Link>
              <Link to="/confidentialite" className="hover:text-emerald-400 transition-colors">
                Politique de confidentialité
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
